import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  Paper,
  IconButton,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import { useForm } from "react-hook-form";
import { useParams, useNavigate } from "react-router-dom";

import WSTextField from "../../../Components/TextBox/WSTextBox";
import WSPhone from "../../../Components/Phone/WSPhone";
import { callAPI, customValidation, Method, validations } from "../../../commonFunctions/CommonFunctions";
import { useSnackbar } from "../../../Components/Snackbar/SnackBarContext";
import ConfirmationPopup from "../../../Components/Confirmation/Confirmation";
import { useLocation } from "react-router-dom";



const Warehouse = () => {
  const [loading, setLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedId, setSelectedId] = useState<string | number>(0);
  const [isPopupOpen, setPopupOpen] = useState(false);
  const { showMessage } = useSnackbar();
  const params = useParams();
  const id = params.id;
  const navigate = useNavigate();

  const location = useLocation();
  const from = location.state?.from;



  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid },
  } = useForm({
    mode: "onChange",
    defaultValues: {
      WarehouseName: "",
      PhoneNumber: "",
      AlternatePhone: "",
      Email: "",
      GSTNumber: "",
      Location: "",
      IsActive: true,
    },
  });

  useEffect(() => {

    if (!id || id === "undefined" || id === "null" || id.trim() === "") {
      setIsEditMode(false);
      reset();
      return;
    }

    setIsEditMode(true);
    fetchWarehouseData(id);
  }, [id, showMessage, reset]);

  const fetchWarehouseData = async (warehouseId: string) => {
    try {
      setLoading(true);

      const response = await callAPI(`warehouses/${warehouseId}`, Method.GET);

      if (response?.success && response.data) {
        const warehouseData = response.data;

        reset({
          WarehouseName: warehouseData.WarehouseName || "",
          PhoneNumber: warehouseData.PhoneNumber || "",
          AlternatePhone: warehouseData.AlternatePhone || "",
          Email: warehouseData.Email || "",
          GSTNumber: warehouseData.GSTNumber || "",
          Location: warehouseData.Location || "",
          IsActive: warehouseData.IsActive !== false,
        });
      } else {
        console.error("❌ No data in response:", response);
        showMessage(response?.message || `Warehouse ${warehouseId} not found`, "error");
        setIsEditMode(false);
      }
    } catch (error: any) {
      console.error("❌ Fetch error:", error.response?.data || error.message);
      showMessage("Failed to load warehouse data", "error");
      setIsEditMode(false);
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data: any) => {
    try {
      setLoading(true);
      const requestBody = {
        WarehouseName: data.WarehouseName?.trim(),
        PhoneNumber: data.PhoneNumber || "",
        AlternatePhone: data.AlternatePhone || "",
        Email: data.Email?.trim() || "",
        Location: data.Location?.trim() || "",
        GSTNumber: data.GSTNumber?.trim() || "",
        IsActive: data.IsActive || true,
      };


      let response;
      if (isEditMode && id) {
        response = await callAPI(`warehouses/${id}`, Method.PATCH, requestBody);
      } else {
        response = await callAPI("warehouses", Method.POST, requestBody);
      }

      if (response?.success) {
        showMessage(
          isEditMode
            ? "Warehouse updated successfully!"
            : "Warehouse created successfully!",
          "success"
        );
        
        if (response?.success) {
          if (from === "openingstock") {
            navigate("/openingstock");
          } else if (from === "adjustment") {
            navigate("/adjustment");
          }
          else if (from === "verification") {
            navigate("/verification");
          }
          else if (from === "stock-return") {
            navigate("/stock-return");
          }
          else if (from === "itemmaster") {
            navigate("/itemmaster");
          }
          else if (from === "stockin") {
            navigate("/stockin");
          }
          else if (from === "stockout") {
            navigate("/stockout");
          }

          else {
            navigate("/warehouse/list");
          }
        }
      } else {
        showMessage(response?.message || "Operation failed", "error");
      }
    } catch (error: any) {
      console.error("❌ Submit error:", error);
      showMessage(error.response?.data?.detail || "Operation failed", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = () => {
    if (!id) return;
    setSelectedId(id);
    setPopupOpen(true);
  };

  return (
    <Box sx={{ p: 2, background: "#f0f0f0", minHeight: "100vh" }}>
      { }
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Box display="flex" alignItems="center" gap={2}>

          <Typography sx={{ fontSize: "20px", fontWeight: 600 }}>
            {isEditMode ? "Edit Warehouse" : "Warehouse Management"}
          </Typography>
        </Box>

        <Box>

          {isEditMode && id && (
            <IconButton
              onClick={handleDeleteClick}
              disabled={loading}
              sx={{
                background: "black",
                color: "#fff",
                "&:hover": { background: "black" },
              }}
            >
              {loading ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon fontSize="small" />}
            </IconButton>
          )}
        </Box>
      </Box>

      { }
      <Paper
        sx={{
          borderRadius: "16px",
          p: 3,
          background: "#fafafa",
          position: "relative",
        }}
      >
        {loading && (
          <Box sx={{
            position: "absolute",
            top: 0, left: 0, right: 0, bottom: 0,
            background: "rgba(255,255,255,0.9)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10,
            borderRadius: "16px",
          }}>
            <CircularProgress />
          </Box>
        )}

        { }
        <Box display="flex" justifyContent="space-between" mb={7}>
          <Typography sx={{ fontSize: "18px", fontWeight: 500 }}>
            {isEditMode ? "Warehouse Details" : "Warehouse Details"}
          </Typography>
          <Tooltip title={isEditMode ? "Update Warehouse" : "Create Warehouse"} arrow>
            <IconButton
              sx={{ background: "#000", color: "#fff" }}
              onClick={handleSubmit(onSubmit)}
              disabled={ loading}
            >
              <SaveIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        <form noValidate>
          <Grid container rowSpacing={8} columnSpacing={5}>
            <Grid item xs={4}>
              <WSTextField
                name="WarehouseName"
                label="Warehouse Name"
                id="WarehouseName"
                control={control}
                rules={{ required: "Warehouse name is required" }}
              />
            </Grid>

            <Grid item xs={4}>
              <WSTextField
                name="Location"
                label="Location"
                id="Location"
                control={control}
                
              />
            </Grid>

            <Grid item xs={4}>
              <WSPhone
                name="PhoneNumber"
                label="Phone Number"
                id="PhoneNumber"
                control={control}
                
              />
            </Grid>

            <Grid item xs={4}>
              <WSPhone
                name="AlternatePhone"
                label="Alternate Phone Number"
                id="AlternatePhone"
                control={control}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <WSTextField
                name="Email"
                label="Email"
                id="Email"
                control={control}
                
              />
            </Grid>

            {/* <Grid item xs={4}>
              <WSTextField
                name="GSTNumber"
                label="GST Number"
                id="GSTNumber"
                control={control}
              />
            </Grid> */}
          </Grid>
        </form>
      </Paper>

      { }
      <ConfirmationPopup
        type="DELETE"
        apiUrl="warehouses" // ensure this matches your warehouse delete endpoint
        id={selectedId}
        confirmMessage="Are you sure you want to delete this warehouse?"
        open={isPopupOpen}
        onClose={() => setPopupOpen(false)}
        onConfirm={async (data) => {
          if (!selectedId) return;

          if (data?.success === true) {
            // Optional: clear grid / state
            // dispatch(resetDataComponent("warehouseGrid")); // uncomment if you use Redux

            showMessage("Warehouse deleted successfully!", "success");
            navigate("/warehouse/list");
          } else {
            showMessage(data?.detail, "error");
          }

          setPopupOpen(false);
        }}
      />
    </Box>
  );
};

export default Warehouse;
